import React, { Fragment } from 'react';
import moment from 'moment';
import { Checkbox, FormControlLabel, FormGroup, Radio, RadioGroup } from '@mui/material';
import { DetailItem } from '../../screens';
import { formAttributeEnums } from '../../../pages/forms/constants';
import { DateRow, PhoneRow } from '../../table';
import { UploadedSignature } from '../../../fragments/schedule/core/common/uploadedSignature';
import { inputsStyle } from '../../inputs/styles';
import { isAutoSignature } from '../../../pages/forms/formDetail/signatureFill.constants';
import { attributeTypes, formatAMPMNeW, Images, sanitizeHtml } from '../../../utils';
import { inputTypeEnums, selectTypeEnums } from '../../../pages/forms/formDetail/attributes/constnats';
import { PrevTableElement } from '../../../pages/forms/formPreview/elements';
import { SessionDataTableElement } from '../../../pages/forms/formDetail/formBody/elements/sessionDataTableElement';
import { AutoSignatureElement } from '../../../pages/forms/formDetail/formBody/elements/autoSignatureElement';
import noHistory from '../../../assets/images/icons/notYet/noHistory.svg';

export const renderNoteValue = (i) => {
    const classes = inputsStyle();

    const startEndTime = (it) => {
        return moment(`Mon 03-Jul-2017, ${it}`, 'ddd DD-MMM-YYYY, hh:mm A').format('hh:mm A');
    };

    if (i?.type === attributeTypes.RICH_TEXT) {
        return (
            <div style={{ marginBottom: 12 }}>
                {i.label && (
                    <p className="note-detail-title" style={{ margin: '8px 0' }}>
                        {i.label}
                    </p>
                )}
                <div className="richtext-readonly-content" dangerouslySetInnerHTML={{ __html: sanitizeHtml(i?.value || '--') }} />
            </div>
        );
    }

    if (i?.type === attributeTypes.INPUT) {
        const label = i?.label || i?.name;

        if (i?.defaultDateType === inputTypeEnums.PHONE) {
            return <DetailItem title={label} value={i?.value ? <PhoneRow phone={i.value + ''} /> : '--'} />;
        }
        if (i?.defaultDateType === inputTypeEnums.DATE) {
            return <DetailItem title={label} value={<DateRow date={i?.value} />} />;
        }
        if (i?.defaultDateType === inputTypeEnums.TIME) {
            return <DetailItem title={label} value={i?.value ? formatAMPMNeW(i.value) || i.value : '--'} />;
        }
        const valueContent = i?.value ? <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(i.value) }} /> : '--';
        return <DetailItem underLine={true} title={label} value={valueContent} />;
    }

    // SELECT — single or multiple
    if (i?.type === attributeTypes.SELECT) {
        const label = i?.label || i?.name;
        if (i?.defaultDateType === selectTypeEnums.MULTIPLE) {
            const names = Array.isArray(i?.value) ? i.value.map((v) => v?.name || v?.value).join(', ') : '';
            return <DetailItem title={label} value={names || '--'} />;
        }
        // SINGLE
        return <DetailItem title={label} value={i?.value?.name || '--'} />;
    }

    // CHECK_BOX
    if (i?.type === attributeTypes.CHECK_BOX) {
        const selected = Array.isArray(i?.value) ? i.value : [];
        return (
            <DetailItem
                wrapperStyles={{ flexDirection: 'column' }}
                title={i?.label || i?.name}
                value={
                    <FormGroup>
                        {i?.properties?.options?.map((option) => (
                            <FormControlLabel
                                key={option.id}
                                control={
                                    <Checkbox
                                        disabled={true}
                                        style={{ color: '#0C66E4', padding: '2px 8px' }}
                                        checked={selected.some((s) => s.id === option.id || s.name === option.name)}
                                    />
                                }
                                label={option.name}
                            />
                        ))}
                    </FormGroup>
                }
            />
        );
    }

    // RADIO_BUTTON
    if (i?.type === attributeTypes.RADIO_BUTTON) {
        return (
            <DetailItem
                wrapperStyles={{ flexDirection: 'column' }}
                title={i?.label || i?.name}
                value={
                    <RadioGroup value={i?.value?.id || ''}>
                        {i?.properties?.options?.map((option) => (
                            <FormControlLabel
                                checked={i?.value?.id === option.id}
                                disabled={true}
                                className={classes.radioInputLabel}
                                key={option.id}
                                value={option.id}
                                control={
                                    <Radio
                                        disableRipple
                                        style={{ color: '#0C66E4', padding: '2px 8px' }}
                                        classes={{
                                            root: classes.radio,
                                            checked: classes.checked,
                                        }}
                                    />
                                }
                                label={option.name}
                            />
                        ))}
                    </RadioGroup>
                }
            />
        );
    }

    if (i?.type === attributeTypes.TABLE) {
        return (
            <div style={{ marginBottom: 12, overflowX: 'auto' }}>
                <p className="note-detail-title" style={{ margin: '8px 0' }}>
                    {i?.label || i?.name}
                </p>
                <PrevTableElement item={i} />
            </div>
        );
    }

    if (i?.type === attributeTypes.SESSION_DATA_TABLE) {
        return (
            <div style={{ marginBottom: 12, overflowX: 'auto' }}>
                <p className="note-detail-title" style={{ margin: '8px 0' }}>
                    {i?.label || i?.name}
                </p>
                <SessionDataTableElement item={i} readOnly={true} />
            </div>
        );
    }

    if (i?.type === attributeTypes.SIGNATURE) {
        // An auto-filling block carries no `value` of its own — it mirrors the
        // appointment — so it renders its own line, empty state included.
        if (isAutoSignature(i?.properties)) {
            return (
                <div style={{ marginTop: 20 }}>
                    <p className="note-detail-title" style={{ margin: '8px 0' }}>
                        {i?.label || i?.name}
                    </p>
                    <AutoSignatureElement item={i} />
                </div>
            );
        }
        if (!i?.value) return null;
        return (
            <div style={{ marginTop: 20 }}>
                <p className="note-detail-title" style={{ margin: '8px 0 8px 0' }}>
                    {i?.label || i?.name}
                </p>
                <UploadedSignature currentFile={{ file: i?.value }} alreadyUploaded={true} noDel={true} />
            </div>
        );
    }

    if (i?.type === attributeTypes.UPLOAD) {
        if (!i?.value) return null;
        return (
            <div style={{ marginTop: 20 }}>
                <p className="note-detail-title" style={{ margin: '8px 0 8px 0' }}>
                    {i?.label || i?.name}
                </p>
                <UploadedSignature currentFile={{ file: i?.value }} alreadyUploaded={true} noDel={true} />
            </div>
        );
    }

    // GRAPH
    // if (i?.type === attributeTypes.GRAPH) {
    //     return (
    //         <div style={{ marginBottom: 12 }}>
    //             <p className="note-detail-title" style={{ margin: '8px 0' }}>{i?.label || i?.name || 'Graph'}</p>
    //             <p style={{ color: '#667085', fontSize: 14 }}>Graph data available in report view.</p>
    //         </div>
    //     );
    // }
    //
    // // TARGET
    // if (i?.type === attributeTypes.TARGET) {
    //     return (
    //         <div style={{ marginBottom: 12 }}>
    //             {i?.label && <p className="note-detail-title" style={{ margin: '8px 0' }}>{i.label}</p>}
    //             {i?.value ? (
    //                 <div
    //                     className="form-text-editor-style"
    //                     dangerouslySetInnerHTML={{ __html: i.value }}
    //                 />
    //             ) : (
    //                 <p style={{ color: '#667085', fontSize: 14 }}>N/A</p>
    //             )}
    //         </div>
    //     );
    // }

    return (
        <div>
            {i?.type === formAttributeEnums.INSTRUCTIONS ? (
                <p className="note-detail-info-title" style={{ marginBottom: 8 }}>
                    {i?.name}
                </p>
            ) : i?.type === formAttributeEnums.TIMEINPUT ? (
                <DetailItem title={i?.name} value={i?.value ? formatAMPMNeW(i?.value) || i?.value : '--'} />
            ) : i?.type === formAttributeEnums.DATEPICKER ? (
                <DetailItem title={i?.name} value={<DateRow date={i?.value} />} />
            ) : i?.type === formAttributeEnums.AVAILABILITY ? (
                i?.value &&
                !!Object.keys(i?.value)?.length && (
                    <div className="org-days-hours">
                        <p className="note-detail-title" style={{ margin: '8px 0 8px 0' }}>
                            {i?.name}
                        </p>
                        {i?.value && Array.isArray(i?.value) === false
                            ? Object.keys(i?.value).map((it, j) => {
                                  return (
                                      !!i?.value[it]?.length && (
                                          <div key={j} className="org-days-hours-box">
                                              {i?.value[it]?.map((k, m) => (
                                                  <Fragment key={m}>
                                                      <DetailItem
                                                          title={it}
                                                          value={
                                                              k?.available ? `${startEndTime(k?.from)} - ${startEndTime(k?.to)}` : 'Closed'
                                                          }
                                                      />
                                                  </Fragment>
                                              ))}
                                          </div>
                                      )
                                  );
                              })
                            : ''}
                    </div>
                )
            ) : i?.type === formAttributeEnums.PHONEINPUT ? (
                <DetailItem title={i?.name} value={i?.value ? <PhoneRow phone={i?.value + ''} /> : '--'} />
            ) : i?.type === formAttributeEnums.NAMEINPUT ? (
                <div style={{ marginBottom: 12 }}>
                    <p className="note-detail-title" style={{ margin: '8px 0 8px 0' }}>
                        {i?.name}
                    </p>
                    <div className="note-detail-body-wrapper">
                        {i?.fields?.map((k, m) => (
                            <Fragment key={m}>
                                <DetailItem
                                    underLine={true}
                                    wrapperStyles={{ width: '49%' }}
                                    title={k?.title}
                                    value={k?.value ? k.value : '--'}
                                />
                            </Fragment>
                        ))}
                    </div>
                </div>
            ) : i?.type === formAttributeEnums.PARAGRAPH ? (
                <div style={{ marginBottom: 12, width: '100%' }}>
                    <div className="note-detail-body-wrapper" style={{ width: '100%' }}>
                        <Fragment>
                            <DetailItem
                                underLine={true}
                                title={i?.name}
                                value={
                                    <p
                                        className="form-text-editor-style"
                                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(i?.value === '<p><br></p>' ? '--' : i?.value?.toString('html')) }}
                                    />
                                }
                            />
                        </Fragment>
                    </div>
                </div>
            ) : i?.type === formAttributeEnums.ADDRESSINPUT ? (
                <div style={{ marginBottom: 12 }}>
                    <p className="note-detail-title" style={{ margin: '8px 0 8px 0' }}>
                        {i?.name}
                    </p>
                    <div className="note-detail-body-wrapper">
                        {i?.fields?.map((k, m) => (
                            <Fragment key={m}>
                                <DetailItem
                                    underLine={true}
                                    wrapperStyles={{ width: '49%' }}
                                    title={k?.title}
                                    value={k?.value ? k.value : '--'}
                                />
                            </Fragment>
                        ))}
                    </div>
                </div>
            ) : i?.type === formAttributeEnums.SIGNATURE || i?.type === formAttributeEnums.UPLOADIMAGE ? (
                i?.value && (
                    <div style={{ marginTop: 20 }}>
                        <p className="note-detail-title" style={{ margin: '8px 0 8px 0' }}>
                            {i?.name}
                        </p>
                        <UploadedSignature currentFile={{ file: i?.value }} alreadyUploaded={true} noDel={true} />
                    </div>
                )
            ) : i?.type === formAttributeEnums.SINGLECHOICE ? (
                <DetailItem
                    wrapperStyles={{ flexDirection: 'column' }}
                    title={i?.name}
                    value={
                        <RadioGroup>
                            {i?.customList?.map((option) => {
                                return (
                                    <FormControlLabel
                                        checked={i?.value === (option?.value || option?.key)}
                                        disabled={true}
                                        className={classes.radioInputLabel}
                                        key={option.key}
                                        value={option?.value}
                                        control={
                                            <Radio
                                                disableRipple
                                                style={{ color: '#0C66E4' }}
                                                classes={{
                                                    root: classes.radio,
                                                    checked: classes.checked,
                                                }}
                                            />
                                        }
                                        label={option.title}
                                    />
                                );
                            })}
                        </RadioGroup>
                    }
                />
            ) : i?.type === formAttributeEnums.MULTIPLECHOICE ? (
                <DetailItem
                    wrapperStyles={{ flexDirection: 'column' }}
                    title={i?.name}
                    value={
                        <FormGroup>
                            {i?.customList?.map((option) => {
                                return (
                                    <FormControlLabel
                                        name={i?.name}
                                        key={option.key}
                                        control={
                                            <Checkbox
                                                disabled={true}
                                                style={{ color: '#0C66E4' }}
                                                checked={i?.value?.includes(option?.value || option?.key)}
                                            />
                                        }
                                        label={option.title}
                                    />
                                );
                            })}
                        </FormGroup>
                    }
                />
            ) : i?.type === formAttributeEnums.SINGLESELECT ? (
                <DetailItem title={i?.name} value={i?.value?.title ? i.value?.title : '--'} />
            ) : i?.type === formAttributeEnums.MULTIPLESELECT ? (
                <DetailItem title={i?.name} value={i?.value ? i.value?.map((i) => i?.title).join(', ') : '--'} />
            ) : (
                <DetailItem underLine={true} title={i?.name || i?.label} value={i?.value ? i.value : '--'} />
            )}
        </div>
    );
};


export const noteViewNotYet = {
    title: 'No Views Yet',
    subTitle: 'This note has not been viewed by any users',
    image: Images.noHistory,
    height: '300px',
};